import { useShellContext } from '@omni/kit';
import { KitTouchableIcon } from '@omni/kit/components';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import Show from '@omni/kit/components/Show';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, View, useWindowDimensions } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import AuthProviderList from '../AuthProviderList';
import {
  HEADER_FOOTER_HEIGHT,
  MIN_MODAL_HEIGHT,
  MIN_MODAL_WIDTH,
} from '../Constants';
import { validateToken } from '../Service/validateToken';
import { IProps } from './types';

const MAX_MODAL_WIDTH = MIN_MODAL_WIDTH;

export default ({
  appKey = '',
  contentStyle,
  coverScreen = true,
  customHeader,
  isVisible = false,
  modalStyle,
  onClose,
  returnUrl = '',
  targetAuthProviderId,
}: IProps): JSX.Element => {
  const { app, tokens, appUrls } = useShellContext();
  const orgKey = app.orgKey;

  const userToken = useMemo(
    () => validateToken(tokens.user, undefined, appKey, orgKey),
    [tokens.user, appKey, orgKey]
  );

  const [hasBackdrop, setHasBackdrop] = useState(true);

  const { sizeClassV2 } = useSizeClass();

  const anchorBottom = sizeClassV2 < SizeClassV2.L;
  const { width: contentWidth } = useWindowDimensions();

  useEffect(() => {
    if (isVisible && userToken) {
      setHasBackdrop(false); // prevent black flicker on dismiss
      onClose?.();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, userToken]);

  return (
    <KitModalV2
      anchorBottom={anchorBottom}
      avoidKeyboard
      coverScreen={coverScreen}
      hasBackdrop={hasBackdrop}
      isVisible={isVisible}
      noScrollView
      scrollViewProps={{
        contentContainerStyle: styles.scrollContainer,
        keyboardShouldPersistTaps: 'handled',
        showsVerticalScrollIndicator: false,
      }}
      onClose={onClose}
      modalStyle={{
        ...styles.modalStyle,
        width: contentWidth,
        height: anchorBottom ? '85%' : '80%',
        ...modalStyle,
      }}
    >
      <SafeAreaProvider>
        <KeyboardAwareScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.scrollContainer}
          keyboardShouldPersistTaps='handled'
          showsVerticalScrollIndicator={false}
          enableOnAndroid={true}
          enableAutomaticScroll={Platform.OS === 'android'}
        >
          <View
            style={[
              {
                padding: Spacing.m,
                paddingVertical: Spacing.m + 30,
                minHeight: MIN_MODAL_HEIGHT,
              },
              !anchorBottom && {
                flex: 1,
                minWidth: MIN_MODAL_WIDTH,
                paddingVertical: Spacing.m,
              },
              contentStyle,
            ]}
          >
            <Show show={!anchorBottom}>
              <View>
                <KitTouchableIcon
                  color={colorForScheme?.({
                    light: Colors.N900,
                    dark: Colors.N0,
                  })}
                  name='remove'
                  onPress={onClose}
                  style={{
                    alignSelf: 'flex-end',
                  }}
                  underlayColor={colorForScheme?.({
                    dark: Colors.N900,
                    default: Colors.N100,
                  })}
                  buttonSize={HEADER_FOOTER_HEIGHT}
                />
              </View>
            </Show>
            <AuthProviderList
              appKey={appKey}
              customHeader={customHeader}
              orgKey={orgKey}
              customReturnUrl={returnUrl}
              targetAuthProviderId={targetAuthProviderId}
              openInNewTab
            />
            <Show show={!anchorBottom}>
              <View style={{ height: HEADER_FOOTER_HEIGHT }} />
            </Show>
          </View>
        </KeyboardAwareScrollView>
      </SafeAreaProvider>
    </KitModalV2>
  );
};

const styles = StyleSheet.create({
  modalStyle: {
    width: '100%',
    maxWidth: MAX_MODAL_WIDTH,
    paddingTop: 0,
    paddingHorizontal: 0,
    paddingBottom: 0,
    alignSelf: 'center',
    overflow: 'hidden',
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
});
